import type { ReactNode } from 'react';
import type { GridStyle } from '../../hanzi-hero/ChineseWritingGrid';

interface HanziGridStylePreviewProps {
  gridStyle: GridStyle;
  className?: string;
  /** Outer box size in px */
  size?: number;
  /** Faint 范字 in the centre (same idea as the practice grid) */
  demoCharacter?: string;
}

/**
 * Read-only miniature of the practice grid lines (matches ChineseWritingGrid geometry).
 */
const HanziGridStylePreview = ({
  gridStyle,
  className = '',
  size = 72,
  demoCharacter,
}: HanziGridStylePreviewProps) => {
  const s = 100;
  const strokeColor = 'rgba(0,0,0,0.22)';
  const strokeWidth = 1.5;
  const dotted = { strokeDasharray: '3 4' as const };

  const lines: ReactNode[] = [];
  if (gridStyle === 'jiugong') {
    for (let i = 1; i < 3; i++) {
      lines.push(
        <line
          key={`v${i}`}
          x1={(i * s) / 3}
          y1={0}
          x2={(i * s) / 3}
          y2={s}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />,
        <line
          key={`h${i}`}
          x1={0}
          y1={(i * s) / 3}
          x2={s}
          y2={(i * s) / 3}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
      );
    }
  } else if (gridStyle === 'tianzige') {
    lines.push(
      <line key="v" x1={s / 2} y1={0} x2={s / 2} y2={s} stroke={strokeColor} strokeWidth={strokeWidth} {...dotted} />,
      <line key="h" x1={0} y1={s / 2} x2={s} y2={s / 2} stroke={strokeColor} strokeWidth={strokeWidth} {...dotted} />
    );
  } else {
    lines.push(
      <line key="v" x1={s / 2} y1={0} x2={s / 2} y2={s} stroke={strokeColor} strokeWidth={strokeWidth} />,
      <line key="h" x1={0} y1={s / 2} x2={s} y2={s / 2} stroke={strokeColor} strokeWidth={strokeWidth} />,
      <line key="d1" x1={0} y1={0} x2={s} y2={s} stroke={strokeColor} strokeWidth={strokeWidth} />,
      <line key="d2" x1={s} y1={0} x2={0} y2={s} stroke={strokeColor} strokeWidth={strokeWidth} />
    );
  }

  return (
    <div
      className={`relative rounded-xl border-2 border-amber-200/80 bg-white shadow-sm flex items-center justify-center shrink-0 overflow-hidden ${className}`}
      style={{ width: size, height: size }}
      aria-hidden
    >
      <svg viewBox="0 0 100 100" className="w-[85%] h-[85%]" preserveAspectRatio="xMidYMid meet">
        <rect x={1} y={1} width={98} height={98} fill="none" stroke={strokeColor} strokeWidth={strokeWidth} />
        {lines}
      </svg>
      {demoCharacter ? (
        <span
          className="absolute inset-0 flex items-center justify-center pointer-events-none select-none leading-none text-gray-300"
          style={{
            fontFamily: 'KaTi Maobi, KaiTi, serif',
            fontSize: `${Math.max(14, size * 0.44)}px`,
          }}
        >
          {demoCharacter}
        </span>
      ) : null}
    </div>
  );
};

export default HanziGridStylePreview;
